import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { WizardStepProps } from '../types/wizard.types';
import { X } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { ComponentReturnTypeMap, Signal, TaskIssue } from '@momentum/types';
import { DatePickerInput } from '@/shared/components';
import { useTranslation } from 'react-i18next';

interface WizardChatbotSignalsProps
  extends WizardStepProps<ComponentReturnTypeMap['WizardChatbotSignals']> {
  placeholder?: string;
  maxLength?: number;
  addMoreText?: string;
  continueText?: string;
  minEntries?: number;
  onScrollToBottom?: () => void;
}

export const WizardChatbotSignals: React.FC<WizardChatbotSignalsProps> = ({
  value,
  onNext,
  onChange,
  placeholder = 'Write here...',
  maxLength,
  addMoreText = 'Add another',
  continueText = 'Continue to next',
  minEntries = 0,
  onScrollToBottom,
}) => {
  const { t } = useTranslation('common');
  const [entries, setEntries] = useState<Partial<Signal | TaskIssue>[]>(
    Array.isArray(value) ? value : []
  );
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [currentText, setCurrentText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to keep chat visible when entries change
  useEffect(() => {
    if (entries.length > 0 && onScrollToBottom) {
      // Use the scroll function passed from parent
      setTimeout(() => {
        onScrollToBottom();
      }, 100);
    }
  }, [entries.length, onScrollToBottom]);

  const handleAddEntry = () => {
    if (currentText.trim()) {
      const newEntries: Partial<Signal | TaskIssue>[] = [
        ...entries,
        customDate
          ? {
              title: currentText.trim(),
              dueDate: customDate.toISOString(),
            }
          : {
              title: currentText.trim(),
            },
      ];
      setEntries(newEntries);
      setCurrentText('');
      setCustomDate(null);
      onChange(newEntries);
    }
  };

  const handleRemoveEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    onChange(newEntries);
  };

  const handleContinue = () => {
    // Only use entries that were explicitly added via "Add" button
    if (entries.length >= minEntries) {
      onNext(entries);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddEntry();
    }
  };

  // Only count entries that were explicitly added, not currentText
  const canContinue = entries.length >= minEntries;

  return (
    <div ref={containerRef} className="space-y-4">
      {/* Show previous entries */}
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((entry, index) => (
            <div
              key={index}
              className="group flex items-start gap-3 p-4 bg-gray-50 hover:bg-gray-100 border border-gray-100 rounded-xl transition-all duration-200 animate-in fade-in slide-in-from-left-2"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {!('dueDate' in entry) ? (
                <div className="flex-1 text-[15px] text-gray-900 leading-relaxed">
                  {(entry as Signal).title}
                </div>
              ) : (
                <>
                  <div className="flex-1 text-[15px] text-gray-900 leading-relaxed">
                    {(entry as TaskIssue).title}
                  </div>
                  <div className="mt-1">
                    <DatePickerInput
                      value={new Date((entry as TaskIssue).dueDate ?? '')}
                      onChange={(_date: Date | null) => undefined}
                      disabled={true}
                      placeholder={t('selectDate', 'Select date')}
                    />
                  </div>
                </>
              )}
              <button
                onClick={() => handleRemoveEntry(index)}
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all duration-200"
                aria-label="Remove entry"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input field */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            type="text"
            value={currentText}
            onChange={(e) => setCurrentText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            maxLength={maxLength}
            className="w-full px-4 py-3 border-gray-200 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
          />
        </div>

        <div className="mt-1">
          <DatePickerInput
            value={customDate}
            onChange={(date: Date | null) => setCustomDate(date)}
            placeholder={t('selectDate', 'Select date')}
          />
        </div>

        <Button
          onClick={handleAddEntry}
          disabled={!currentText.trim()}
          variant="outline"
          className="px-5 py-3 rounded-xl border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 transition-all duration-200"
        >
          {addMoreText}
        </Button>
      </div>

      {/* Continue button - prominent CTA */}
      <Button
        onClick={handleContinue}
        disabled={!canContinue}
        className={cn(
          'w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-sm hover:shadow transition-all duration-200',
          !canContinue && 'opacity-40 cursor-not-allowed hover:bg-blue-600'
        )}
      >
        {continueText}
      </Button>
    </div>
  );
};
